import { HttpEvent, HttpEventType } from '@angular/common/http';
import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { sha512 } from 'js-sha512';
import { Observable, from, of } from 'rxjs';
import { catchError, delay, finalize, mergeMap, retryWhen, tap } from 'rxjs/operators';
import { MaxcharPipe } from 'src/app/pipes/maxchar.pipe';
import { AlertService } from 'src/app/services/alert.service';
import { ApiService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { FileUploadModalService } from 'src/app/services/file-upload-modal.service';
import { UtilService } from 'src/app/services/util.service';
import { PdfCheckService } from 'src/app/services/pdf-check.service';
import { FileValidationService } from 'src/app/services/file-validation.service';

@Component({
  selector: 'app-file-upload-modal',
  templateUrl: './file-upload-modal.component.html',
  styleUrls: ['./file-upload-modal.component.scss'],
  providers: [MaxcharPipe]
})
export class FileUploadModalComponent implements OnInit {


  fileUploadProgress:any = {};
  fileUploadErrors:any = {};
  isFileUploading = false;
  isFileUploaded = false;
  category:any;
  singlePerson:number = 0;
  isMultiPersonHovering: boolean;
  isSinglePersonHovering: boolean;
  files: File[] | any[] = [];
  isRetry:boolean = false;
  batchId: string | null = null;

  constructor(
    private router:Router,
    private as:AlertService,
    public fileUploadModalService:FileUploadModalService,
    public dialogRef: MatDialogRef<FileUploadModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private api:ApiService,
    public util:UtilService,
    private ref: ChangeDetectorRef,
    private maxcharPipe: MaxcharPipe,
    private pdfCheckService: PdfCheckService,
    private fileValidationService: FileValidationService,
    private authService: AuthService) {
      this.files = Array.isArray(data) ? data : Array.from(data);
    }


  ngOnInit(): void {
    try {
      let currentPath = this.router.url.split("_")[1];
      this.category = (""+currentPath).toUpperCase();

      // Auto-select category if user has access to only one
      const user = this.authService.getUser();
      const categoryMap = [
        { value: 'SR', authKey: 'SR' },
        { value: 'SPED', authKey: 'SPED' },
        { value: 'ER', authKey: 'HR' },
        { value: 'AR', authKey: 'AR' }
      ];
      const accessibleCategories = categoryMap.filter(c => user.recordSeries.indexOf(c.authKey) > -1);
      if (accessibleCategories.length === 1) {
        this.category = accessibleCategories[0].value;
      }
    } catch (error) {
      this.as.toast(""+error);
    }
  }

  onFilesAdded(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target.files) {
        const newFiles = Array.from(target.files);
        this.addFilesToList(newFiles);
    }
  }

  onFilesDragged(files: any) {
    const newFiles = Array.isArray(files) ? files : Array.from(files);
    this.addFilesToList(newFiles);
  }

  addFilesToList(newFiles: File[]) {
    let updatedFiles = [...this.files];
    newFiles.forEach(newFile => {
        if (!updatedFiles.some(existingFile => existingFile.name === newFile.name && existingFile.size === newFile.size)) {
            updatedFiles.push(newFile);
        }
    });
    this.files = updatedFiles;
    this.isFileUploaded = false; // Re-enable upload button
  }

  isFileGettingUploaded(fileUploadProgress:any){
    return (fileUploadProgress < 100 && fileUploadProgress > 0);
  }

  calculateProgress(e:any) {
    let p = Math.round(e.loaded / e.total * 100);
    if (p > 1) {
      return p;
    }else{
      if(isNaN(p)){
        return 100;
      }
      return 0;
    }
  }

  async checkPdfFile(file: File): Promise<{ canUpload: boolean; errorMessage?: string }> {
    // Skip check for non-PDF files
    if (!this.pdfCheckService.isPdfFile(file)) {
      return { canUpload: true };
    }

    try {
      const buffer = await file.arrayBuffer();
      const result = await this.pdfCheckService.checkPdf(buffer);

      switch (result) {
        case 'not-encrypted':
          return { canUpload: true };
        
        case 'encrypted-no-password':
          return { 
            canUpload: false, 
            errorMessage: "Could not upload this file. File appears to be encrypted." 
          };
        
        case 'password-protected':
          return { 
            canUpload: false, 
            errorMessage: "Could not upload this file. File appears to be encrypted or password protected." 
          };
        
        default:
          return { canUpload: true };
      }
    } catch (error: any) {
      // Handle corruption or other errors
      return { 
        canUpload: false, 
        errorMessage: "Could not upload this file. File appears to be corrupted." 
      };
    }
  }


  calculateSHA512(file:File) {

    return new Promise((resolve, reject) => {

        let reader = new FileReader();
        var batch = 1024 * 1024 * 2;
        var start = 0;
        var total = file.size;
        var current:any = sha512;

        reader.onload = function (event:any) {
          try {
            current = current.update(event.target.result);
            asyncUpdate();
          } catch(e) {
            reject(e);
          }
        };

        var asyncUpdate = function () {
          if (start < total) {
            var end = Math.min(start + batch, total);
            reader.readAsArrayBuffer(file.slice(start, end));
            start = end;
          } else {
            resolve(current.hex());
          }
        };
        asyncUpdate();
      });

  }

  checkIfUploadIsInProgress(){
    let inProgress = false;
    for (let key in this.fileUploadProgress) {
      if(this.fileUploadProgress[key] < 100 && this.fileUploadProgress[key] > 0){
        inProgress = true;
      }
    }
    return inProgress;
  }

  checkIfAllFilesUploaded(){
    let uploaded = true;
    for (let key in this.fileUploadProgress) {
      if(this.fileUploadProgress[key] >= 100 || this.fileUploadProgress[key] < 0){
        uploaded = false;
      }
    }
    return uploaded;
  }

  async retryUpload(file:File){
    this.isRetry = true;
    
    // Validate file type
    const validationResult = this.fileValidationService.isFileTypeSupported(file);
    if (!validationResult.isValid) {
      this.fileUploadErrors[file.name] = validationResult.errorMessage!;
      this.fileUploadProgress[file.name] = -1;
      return;
    }

    // Check PDF file for issues
    const checkResult = await this.checkPdfFile(file);
    if (!checkResult.canUpload) {
      this.fileUploadErrors[file.name] = checkResult.errorMessage!;
      this.fileUploadProgress[file.name] = -1;
      return;
    }

    let fileInfo:any = {};
    fileInfo.sha512 = await this.calculateSHA512(file);
    fileInfo.batchId = this.batchId ? this.batchId : this.util.uuid();
    if(!this.batchId) this.batchId = fileInfo.batchId;
    fileInfo.batchCount = this.data.length;
    fileInfo.uploadedFileId = 0;
    fileInfo.category = this.category;
    fileInfo.isSinglePerson = (this.singlePerson == 1);

    this.api.uploadFile(file, fileInfo).subscribe((event: HttpEvent<any>)=>{
      switch (event.type) {
        case HttpEventType.Sent:
          break;
        case HttpEventType.ResponseHeader:
          break;
        case HttpEventType.UploadProgress:
          this.fileUploadProgress[file.name] = this.calculateProgress(event) > this.fileUploadProgress[file.name] ? this.calculateProgress(event) : this.fileUploadProgress[file.name];
          this.fileUploadErrors[file.name] = null
          break;
        case HttpEventType.Response:
          break;
        default:
          this.ref.detectChanges();
      }
    }, (err:any) => {
      this.fileUploadProgress[file.name] = -1;
      this.fileUploadErrors[file.name] = err.statusText?err.statusText:"Could not upload file.";
    }, ()=>{
      this.isRetry = false;
    });

  }

  async upload(file?: File) {
    if(!file && this.files.length < 1){
      return;
    }
    if(!this.batchId){
      this.batchId = this.util.uuid();
    }
    let subs = [];
    this.isFileUploading = true;

    for (let i = 0; i < this.files.length; i++) {
      const fileObj = this.files[i];

      if(this.fileUploadProgress[fileObj.name] === 100) {
        continue;
      }

      this.fileUploadProgress[fileObj.name] = 1;

      // Validate file type
      const validationResult = this.fileValidationService.isFileTypeSupported(fileObj);
      if (!validationResult.isValid) {
        this.fileUploadErrors[fileObj.name] = validationResult.errorMessage!;
        this.fileUploadProgress[fileObj.name] = -1;
        continue;
      }

      // Check PDF file for issues
      const checkResult = await this.checkPdfFile(fileObj);
      if (!checkResult.canUpload) {
        this.fileUploadErrors[fileObj.name] = checkResult.errorMessage!;
        this.fileUploadProgress[fileObj.name] = -1;
        continue;
      }
      
      let fileInfo:any = {};
      fileInfo.sha512 = await this.calculateSHA512(fileObj);
      fileInfo.batchId = this.batchId;
      fileInfo.batchCount = this.files.length;
      fileInfo.uploadedFileId = i;
      fileInfo.category = this.category;
      fileInfo.isSinglePerson = (this.singlePerson == 1);

      let fileUploadObs:Observable<HttpEvent<Object>>  = this.api.uploadFile(fileObj, fileInfo);
      subs.push({fileUploadObs, fileObj});
    }

    let completedCount = this.files.filter(f => this.fileUploadProgress[f.name] === 100).length;
    from(subs).pipe(
      mergeMap(({fileUploadObs, fileObj}) => fileUploadObs.pipe(
        tap((res: any) => {
          if(res.loaded > 1){
            this.fileUploadProgress[fileObj.name] = this.calculateProgress(res);
            if(this.fileUploadProgress[fileObj.name] >= 100){
              completedCount++;
            }
          }
        }),
        retryWhen(errors => errors.pipe(
          mergeMap((error, index) => {
            if (index === 0) {
              return of(error);
            } else {
              this.fileUploadProgress[fileObj.name] = -1;
              this.fileUploadErrors[fileObj.name] = "Retry failed. " + (error.statusText ? error.statusText : "Could not upload file.");
              const errorMessage = "File upload operation failed for "+fileObj.name+"! due to "+(error.statusText ? error.statusText : " An error occurred");
              this.as.toast(this.maxcharPipe.transform(errorMessage, 100), "Close", 5000);
              throw error;
            }
          })
        )),
        catchError(error => {
          this.fileUploadProgress[fileObj.name] = -1;
          this.fileUploadErrors[fileObj.name] = error.statusText ? error.statusText : "Could not upload file.";
          const errorMessage = "File upload operation failed for "+fileObj.name+"! due to "+(error.statusText ? error.statusText : " An error occurred");
          this.as.toast(this.maxcharPipe.transform(errorMessage, 100), "Close", 5000);
          throw error;
        }),
        delay(1000) // delay in milliseconds
      )),
      finalize(() => {
        this.isFileUploading = false;
        if (completedCount === this.files.length) {
          this.isFileUploaded = true;
        }
      })
    ).subscribe();
  }

  disableUpload(){
    return this.checkIfUploadIsInProgress();//(this.uploadStatus!=-1) || this.singlePerson < 1;
  }

  disableClose(){
    return this.checkIfUploadIsInProgress();
  }

  deleteFile(fileToDelete: any) {
    this.files = this.files.filter(file => file !== fileToDelete);
    delete this.fileUploadProgress[fileToDelete.name];
    delete this.fileUploadErrors[fileToDelete.name];
  }

  canShowRetrySpinner(file: any): boolean {
    return this.fileUploadProgress[file.name] && 
           this.fileUploadProgress[file.name] !== 100 && 
           this.fileUploadProgress[file.name] !== 0 && 
           this.fileUploadProgress[file.name] !== -1 && 
           this.isRetry;
  }

  canShowDeleteButton(file: any): boolean {
    // Don't show delete button if file has an error (failed upload shows retry instead)
    if (this.fileUploadErrors[file.name]) {
      return false;
    }
    return !this.fileUploadProgress[file.name] || this.fileUploadProgress[file.name] <= 0;
  }
}

